import { portalGet, portalPost } from '@/features/portal/utils/portalRequest';

import type {
  ApiDataEnvelope,
  ApiErrorEnvelope,
  CadastroAdvogadoConviteInput,
  ConviteAdvogadoAceiteResultado,
  ConviteAdvogadoPreview,
  MarcoAdvogado,
  NotificacaoAdvogado,
  PainelAdvogadoApi,
  PortalAdvogadoDashboard,
  PortalAdvogadoScenario,
  ProcessoAdvogadoAccessResult,
  ProcuracaoResumo,
  ProcuracaoUploadInput,
  ProcuracaoUploadResult,
} from '../types';
import { buildMarcosCalendario } from '../utils/calendarioFixtures';

const ARBITRAL_PORTAL_BASE = '/arbitral/portal';

export class PortalAdvogadoApiError extends Error {
  readonly codigo: string;
  readonly status: number;

  constructor(mensagem: string, codigo: string, status: number) {
    super(mensagem);
    this.name = 'PortalAdvogadoApiError';
    this.codigo = codigo;
    this.status = status;
  }
}

export async function unwrapData<T>(response: Response, fallbackMessage: string): Promise<T> {
  const raw: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const erro = (raw as ApiErrorEnvelope | null)?.error;
    throw new PortalAdvogadoApiError(
      erro?.message ?? fallbackMessage,
      erro?.code ?? 'ERRO_DESCONHECIDO',
      response.status,
    );
  }
  return ((raw as ApiDataEnvelope<T> | null)?.data ?? raw) as T;
}

const mapPainelParaDashboard = (painel: PainelAdvogadoApi): PortalAdvogadoDashboard => ({
  clientes: painel.clientes,
  processos: painel.processos,
  processosLiberados: painel.processosLiberados,
});

interface ProcuracaoEnviadaApi {
  solicitacaoId: string;
  procuracao: ProcuracaoResumo;
}

interface NotificacaoInboxApi {
  id: string;
  title: string;
  subtitle?: string | null;
  entityId: string;
  status: 'UNREAD' | 'READ' | 'DISMISSED' | 'ARCHIVED';
  occurredAt: string;
}

const varianteDaNotificacao = (titulo: string): NotificacaoAdvogado['variante'] => {
  if (titulo === 'Procuração aprovada') return 'success';
  if (titulo === 'Procuração precisa ser reenviada') return 'error';
  return 'info';
};

const mapNotificacao = (item: NotificacaoInboxApi): NotificacaoAdvogado => ({
  id: item.id,
  titulo: item.title,
  descricao: item.subtitle ?? '',
  processoId: item.entityId,
  criadaEm: item.occurredAt,
  lida: item.status !== 'UNREAD',
  variante: varianteDaNotificacao(item.title),
});

export const portalAdvogadoService = {
  previewConvite: async (token: string): Promise<ConviteAdvogadoPreview> => {
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/advogados/convites/validar`,
      { token },
      true,
    );
    return unwrapData<ConviteAdvogadoPreview>(response, 'Não foi possível validar o convite.');
  },

  aceitarConvite: async (
    input: CadastroAdvogadoConviteInput,
  ): Promise<ConviteAdvogadoAceiteResultado> => {
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/advogados/convites/aceitar`,
      input,
      true,
    );
    return unwrapData<ConviteAdvogadoAceiteResultado>(
      response,
      'Não foi possível concluir o cadastro.',
    );
  },

  obterDashboard: async (): Promise<PortalAdvogadoDashboard> => {
    const response = await portalGet(`${ARBITRAL_PORTAL_BASE}/advogados/painel`);
    const painel = await unwrapData<PainelAdvogadoApi>(
      response,
      'Não foi possível carregar seus clientes e processos.',
    );
    return mapPainelParaDashboard(painel);
  },

  obterProcesso: async (processoId: string): Promise<ProcessoAdvogadoAccessResult> => {
    const response = await portalGet(`${ARBITRAL_PORTAL_BASE}/advogados/processos/${processoId}`);
    if (response.status === 404) return { kind: 'not_found' };
    return unwrapData<ProcessoAdvogadoAccessResult>(
      response,
      'Não foi possível carregar o processo.',
    );
  },

  enviarProcuracao: async (input: ProcuracaoUploadInput): Promise<ProcuracaoUploadResult> => {
    const formData = new FormData();
    formData.append('arquivo', input.file, input.file.name);
    const response = await portalPost(
      `${ARBITRAL_PORTAL_BASE}/advogados/processos/${input.processoId}/procuracao`,
      formData,
    );
    const enviada = await unwrapData<ProcuracaoEnviadaApi>(response, 'O envio não foi concluído.');
    return { processoId: input.processoId, procuracao: enviada.procuracao };
  },

  obterNotificacoes: async (escritorioId: string): Promise<NotificacaoAdvogado[]> => {
    const response = await portalGet('/notifications', { limit: 30 }, false, {
      'X-Workspace-Id': escritorioId,
    });
    const inbox = await unwrapData<{ items?: NotificacaoInboxApi[] }>(
      response,
      'Não foi possível carregar as notificações.',
    );
    return (inbox.items ?? []).map(mapNotificacao);
  },

  marcarNotificacoesLidas: async (escritorioId: string): Promise<void> => {
    const response = await portalPost('/notifications/read-all', {}, false, {
      'X-Workspace-Id': escritorioId,
    });
    await unwrapData<{ updated?: number }>(
      response,
      'Não foi possível marcar as notificações como lidas.',
    );
  },

  obterCalendario: async (scenario: PortalAdvogadoScenario): Promise<MarcoAdvogado[]> => {
    if (scenario === 'erro') {
      throw new Error('Não foi possível carregar seu calendário.');
    }
    if (scenario === 'vazio') return [];
    return buildMarcosCalendario();
  },
};
